// chartDomain.js — data-driven axis-domain helpers for the recharts plots.
//
// Live-user feedback (2026-07): a perovskite run (Voc ≈ 1.24 V) rendered
// with the JV x-axis hardcoded to [0, 1.1] so the zero crossing (the Voc
// intercept) was off-screen. Every chart must derive BOTH axis domains
// from the actual data extents with a small (~5 %) padding, and the JV
// x-domain must always extend past the curve's zero crossing.
//
// A "series" here is an array of {V, J} points (already finite-filtered),
// sorted or unsorted — helpers sort defensively where it matters.

// ── Zero crossing ──────────────────────────────────────────────────────────
// Linear-interpolated voltage of the LAST sign change of J when scanning
// in ascending V. For a light JV curve (J < 0 photocurrent convention)
// this is the Voc intercept. Returns null when the curve never crosses.
export function findZeroCrossing(points) {
  if (!points || points.length < 2) return null;
  const pts = [...points].sort((a, b) => a.V - b.V);
  let crossing = null;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i];
    if (!isFinite(a.J) || !isFinite(b.J)) continue;
    if (a.J === 0) { crossing = a.V; continue; }
    if ((a.J < 0 && b.J >= 0) || (a.J > 0 && b.J <= 0)) {
      const f = -a.J / (b.J - a.J);
      crossing = a.V + f * (b.V - a.V);
    }
  }
  return crossing;
}

// ── X domain for JV-style plots ────────────────────────────────────────────
// Derives [xLo, xHi] from the union of one or more JV series:
//   • xLo covers the data minimum (and 0 V) minus padding;
//   • xHi covers the latest zero crossing plus headroom, capped at the
//     data maximum, plus padding — the Voc intercept is always visible
//     without dragging in the entire forward-bias blow-up region.
// Returns null when there is no data.
export function jvXDomain(seriesList, { padFrac = 0.05 } = {}) {
  const all = (seriesList || []).flat().filter(p => p && isFinite(p.V) && isFinite(p.J));
  if (!all.length) return null;
  let vMin = Infinity, vMax = -Infinity;
  for (const p of all) {
    if (p.V < vMin) vMin = p.V;
    if (p.V > vMax) vMax = p.V;
  }
  // Latest crossing across all series (each saved / overlay curve keeps
  // its own Voc intercept on screen).
  let cross = null;
  for (const s of seriesList) {
    const c = findZeroCrossing(s);
    if (c != null && (cross == null || c > cross)) cross = c;
  }
  const span0 = Math.max(vMax - vMin, 1e-6);
  let hi = vMax;
  if (cross != null) {
    const headroom = Math.max(0.03, 0.06 * span0);
    hi = Math.min(vMax, cross + headroom);
    if (hi < cross) hi = cross; // never end before the crossing itself
  }
  const lo = Math.min(vMin, 0);
  const pad = padFrac * Math.max(hi - lo, 1e-6);
  return [lo - pad, hi + pad];
}

// ── Y domain for JV-style plots ────────────────────────────────────────────
// Extents of J over the points inside the [xLo, xHi] window, padded ~5 %.
// The forward-bias blow-up is capped at |dataMin| (≈ |Jsc|) so a spike
// just past Voc cannot squash the photocurrent plateau; the zero line is
// always inside the domain so the crossing is visible.
// Returns null when there is no data in the window.
export function jvYDomain(seriesList, xLo, xHi, { padFrac = 0.05 } = {}) {
  const js = [];
  for (const s of seriesList || []) {
    for (const p of s || []) {
      if (p && isFinite(p.V) && isFinite(p.J) && p.V >= xLo && p.V <= xHi) js.push(p.J);
    }
  }
  if (!js.length) return null;
  let rawMin = Infinity, rawMax = -Infinity;
  for (const j of js) {
    if (j < rawMin) rawMin = j;
    if (j > rawMax) rawMax = j;
  }
  let hi = rawMax;
  if (rawMin < 0) hi = Math.min(rawMax, Math.abs(rawMin)); // cap blow-up
  hi = Math.max(hi, 0);            // keep J = 0 (the crossing) in view
  const lo = Math.min(rawMin, 0);
  const pad = padFrac * Math.max(hi - lo, 1e-6);
  return [lo - pad, hi + pad];
}

// ── Generic padded extent ──────────────────────────────────────────────────
// [min, max] of a plain numeric array padded by padFrac on both sides.
// Returns null for empty input.
export function paddedExtent(values, padFrac = 0.05) {
  const fin = (values || []).filter(v => v != null && isFinite(v));
  if (!fin.length) return null;
  let lo = Infinity, hi = -Infinity;
  for (const v of fin) {
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  const pad = padFrac * Math.max(hi - lo, 1e-6);
  return [lo - pad, hi + pad];
}

// ── Nice ticks ─────────────────────────────────────────────────────────────
// 1/2/5×10ⁿ step that yields roughly `target` ticks over `span`.
export function niceStep(span, target = 6) {
  const s = Math.abs(span);
  if (!isFinite(s) || s <= 0) return 1;
  const raw = s / Math.max(target, 1);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const mult = norm < 1.5 ? 1 : norm < 3.5 ? 2 : norm < 7.5 ? 5 : 10;
  return mult * mag;
}

// Tick values at multiples of `step` inside [lo, hi].
export function buildTicks(lo, hi, step) {
  if (!isFinite(lo) || !isFinite(hi) || !isFinite(step) || step <= 0 || hi <= lo) return undefined;
  const out = [];
  let v = Math.ceil(lo / step) * step;
  for (; v <= hi + 1e-9 && out.length < 200; v += step) {
    out.push(Math.round(v * 1e9) / 1e9);
  }
  return out.length ? out : undefined;
}
